import React, {useMemo} from 'react';
import {useTranslation} from "react-i18next";
import Modal from "react-bootstrap/Modal";
import {Field, Form, Formik} from "formik";
import Button from "react-bootstrap/Button";
import {OrderChangeStatusDialogProps, TimeForReadyOptionsProps} from "./types";
import BootstrapForm from "react-bootstrap/Form";
import CreatableSelectWrapper from "../../../../common/components/CreatableSelectWrapper";
import DateTimePicker from "../../../../common/components/DateTimePicker";
import {format, subMinutes} from "date-fns";
import {
  mapPickerDateToServerDate,
  parseServerDate,
  UsedDateTimeFormats
} from "../../../../common/utils/dateTimeHelpers";

/**
 * Диалог подтверждения изменения статуса
 * (и, при необходимости, ввода дополнительных полей для изменения статуса)
 */
const OrderChangeStatusDialog: React.VFC<OrderChangeStatusDialogProps> = ({
  info,
  closeHandler,
  submitHandler
}) => {
  const {t} = useTranslation();

  // const [timeForReadyOptions, setTimeForReadyOptions] = useState<TimeForReadyOptionsProps[]>([]);
  //
  // useEffect(() => {
  //   const options = [];
  //   for (let a = 5; a <= 180; a+=5){
  //     options.push({label:t(`features.orders.minutes`, {minutes: a}), value: a})
  //   }
  //
  //   setTimeForReadyOptions(options);
  // }, [t])
  
  const timeForReadyOptions = useMemo<TimeForReadyOptionsProps[]>(() => {
    const options = [];
    for (let a = 5; a <= 180; a += 5){
      options.push({label:t(`features.orders.minutes`, {minutes: a}), value: a})
    }
    
    return options;
  }, [t])

  const isTimeDeliveryForm = info?.additionalForm === "timeDeliveryForm" || info?.additionalForm === "changeTimeDeliveryForm";

  const timeForDeliveryOptions = useMemo<TimeForReadyOptionsProps[]>(() => {
    const options = [];
    for (let a = 5; a <= 180; a += 5){
      options.push({label:t(`features.orders.minutes`, {minutes: a}), value: a})
    }

    return options;
  }, [t])

  const defaultDateTimeReadyStart = useMemo(() => {
    if (!info?.isShowDateTimeReadyStart) return undefined;
    if (info?.dateTimeReadyStart) {
      return format(parseServerDate(info.dateTimeReadyStart), UsedDateTimeFormats.DisplayedInPicker);
    }
    if (info?.deliveryDateTime) {
      const deliveryDate = parseServerDate(info.deliveryDateTime);
      const totalMinutes = (info?.timeForReady ?? 0) + (info?.timeForDelivery ?? 0) + 10;
      return format(subMinutes(deliveryDate, totalMinutes), UsedDateTimeFormats.DisplayedInPicker);
    }
    return "";
  }, [info]);

  const dateTimeReadyStartObject = info?.isShowDateTimeReadyStart
    ? { dateTimeReadyStart: defaultDateTimeReadyStart }
    : {};

  return <Modal show={!!info} onHide={closeHandler} centered>
    <Modal.Header closeButton>
      {info?.additionalForm === "timeReadyForm" ?
          <Modal.Title>{t(`features.orders.orderPreparationTime`)}</Modal.Title> :
        isTimeDeliveryForm ?
          <Modal.Title>{t(`features.orders.orderDeliveryTime`)}</Modal.Title> :
          <Modal.Title>{t(`features.orders.orderStatusChange`)}</Modal.Title>
      }
    </Modal.Header>
    <Formik
      initialValues={{
        timeForReady: info?.timeForReady ? info?.timeForReady : null,
        timeForDelivery: info?.timeForDelivery ? info?.timeForDelivery : null,
        ...dateTimeReadyStartObject
      }}
      onSubmit={(values) => {

        let form = null;

        if (values.timeForReady) {
          if (info?.additionalForm === "timeReadyForm") {

            const dateTimeReadyStart = (!!values.dateTimeReadyStart && values.dateTimeReadyStart.length > 0)
              ? {dateTimeReadyStart: mapPickerDateToServerDate(values.dateTimeReadyStart)}
              : {};

            form = {
              timeForReady: values.timeForReady,
              ...dateTimeReadyStart
            }
          }
        }

        if (values.timeForDelivery && isTimeDeliveryForm) {
          form = {
            timeForDelivery: values.timeForDelivery,
          }
        }

        submitHandler(form);
      }}>
      {({values, setFieldValue}) => {
        return <Form>
          <Modal.Body>

            {info?.additionalForm !== "timeReadyForm" && !isTimeDeliveryForm && <>
            <div>
              {t(
                  'features.orders.reallyChangeStatus',
                  {
                    status: info?.statusTitle,
                  }
              )}
            </div>
            </>
            }

            {info?.additionalForm === "timeReadyForm" && <>
              <CreatableSelectWrapper
                  options={timeForReadyOptions}
                  onChange={data => {
                    setFieldValue('timeForReady', data?.value ?? null);
                  }}
                  onCreateOption={(inputValue) => {
                    const num = parseInt(inputValue, 10);
                    if (!isNaN(num) && num > 0) {
                      setFieldValue('timeForReady', num);
                    }
                  }}
                  value={
                    values.timeForReady
                      ? timeForReadyOptions.find((x: TimeForReadyOptionsProps) => x.value === values.timeForReady)
                        ?? {label: t(`features.orders.minutes`, {minutes: values.timeForReady}), value: values.timeForReady}
                      : null
                  }
                  isValidNewOption={(inputValue) => {
                    const num = parseInt(inputValue, 10);
                    return !isNaN(num) && num > 0 && String(num) === inputValue.trim();
                  }}
                  formatCreateLabel={(inputValue) => t(`features.orders.minutes`, {minutes: inputValue})}
                  isClearable={false}
                  placeholder={t(`features.orders.choseReadyTime`)}
              />
              <div className="d-flex mt-1">
                <Button
                    size={'sm'}
                    onClick={() => {
                      setFieldValue('timeForReady', timeForReadyOptions[2].value)
                    }}
                >{timeForReadyOptions[2].label}</Button>
                <Button
                    size={'sm'}
                    onClick={() => {
                      setFieldValue('timeForReady', timeForReadyOptions[5].value)
                    }}
                    className={'mx-1'}
                >{timeForReadyOptions[5].label}</Button>
                <Button
                    size={'sm'}
                    onClick={() => {
                      setFieldValue('timeForReady', timeForReadyOptions[11].value)
                    }}
                >{timeForReadyOptions[11].label}</Button>
              </div>
              {info?.isShowDateTimeReadyStart &&
                <>
                  <BootstrapForm.Label className="mt-2" htmlFor="message">{t(`features.orders.orderPreparationTimeStart`)}</BootstrapForm.Label>
                  <Field
                      name='dateTimeReadyStart'
                      component={DateTimePicker}
                      onChange={(name: string, value: Date | null) => {
                        if (value) {
                          const string = format(value, UsedDateTimeFormats.DisplayedInPicker);

                          setFieldValue('dateTimeReadyStart', string);
                          //setFieldValue('dateTimeReadyStartSuffix', format(value, ":ssx"));
                        } else {
                          setFieldValue('dateTimeReadyStart', '')
                          //setFieldValue('dateTimeReadyStartSuffix', '')
                        }
                      }}
                  />
                </>
              }
            </>}

            {isTimeDeliveryForm && <>
              <CreatableSelectWrapper
                  options={timeForDeliveryOptions}
                  onChange={data => {
                    setFieldValue('timeForDelivery', data?.value ?? null);
                  }}
                  onCreateOption={(inputValue) => {
                    const num = parseInt(inputValue, 10);
                    if (!isNaN(num) && num > 0) {
                      setFieldValue('timeForDelivery', num);
                    }
                  }}
                  value={
                    values.timeForDelivery
                      ? timeForDeliveryOptions.find((x: TimeForReadyOptionsProps) => x.value === values.timeForDelivery)
                        ?? {label: t(`features.orders.minutes`, {minutes: values.timeForDelivery}), value: values.timeForDelivery}
                      : null
                  }
                  isValidNewOption={(inputValue) => {
                    const num = parseInt(inputValue, 10);
                    return !isNaN(num) && num > 0 && String(num) === inputValue.trim();
                  }}
                  formatCreateLabel={(inputValue) => t(`features.orders.minutes`, {minutes: inputValue})}
                  isClearable={false}
                  placeholder={t(`features.orders.choseDeliveryTime`)}
              />
              <div className="d-flex mt-1">
                <Button
                    size={'sm'}
                    onClick={() => {
                      setFieldValue('timeForDelivery', timeForDeliveryOptions[2].value)
                    }}
                >{timeForDeliveryOptions[2].label}</Button>
                <Button
                    size={'sm'}
                    onClick={() => {
                      setFieldValue('timeForDelivery', timeForDeliveryOptions[5].value)
                    }}
                    className={'mx-1'}
                >{timeForDeliveryOptions[5].label}</Button>
                <Button
                    size={'sm'}
                    onClick={() => {
                      setFieldValue('timeForDelivery', timeForDeliveryOptions[11].value)
                    }}
                >{timeForDeliveryOptions[11].label}</Button>
              </div>
            </>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeHandler}>
              {t('common.actionCancel')}
            </Button>
            <Button
              disabled={
                (info?.additionalForm === "timeReadyForm" && (!values.timeForReady || (info?.isShowDateTimeReadyStart && !values.dateTimeReadyStart)))
                || (isTimeDeliveryForm && !values.timeForDelivery)
              }
              variant="outline-primary"
              type="submit">
              {t('common.actionSubmit')}
            </Button>
          </Modal.Footer>
        </Form>
      }}
    </Formik>
  </Modal>;
};

export default OrderChangeStatusDialog;
